import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type {
  EntityValuesUpdateBody,
  Equipment,
  EquipmentBody,
  EquipmentGroup,
  EquipmentGroupBody,
  EquipmentType,
  EquipmentTypeBody,
} from '@erp/shared';

const RESOURCES_KEY = ['erp', 'resources'] as const;

// ── Tipos de recurso ────────────────────────────────────────────────────────

export function useCreateEquipmentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EquipmentTypeBody) => {
      const res = await api<EquipmentType>('/resources/types', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}

export type EquipmentTypeUpdateBody = EquipmentTypeBody & { active: boolean };

export function useUpdateEquipmentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: EquipmentTypeUpdateBody }) => {
      const res = await api<EquipmentType>(`/resources/types/${id}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}

// ── Grupos de recursos ──────────────────────────────────────────────────────

export function useCreateEquipmentGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EquipmentGroupBody) => {
      const res = await api<EquipmentGroup>('/resources/groups', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}

export function useUpdateEquipmentGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: EquipmentGroupBody }) => {
      const res = await api<EquipmentGroup>(`/resources/groups/${id}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}

export function useDeleteEquipmentGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: number) => {
      const res = await api<void>(`/resources/groups/${groupId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}

// ── Recursos ────────────────────────────────────────────────────────────────

export function useCreateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EquipmentBody) => {
      const res = await api<Equipment>('/resources', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}

export function useUpdateEquipment(equipmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EquipmentBody) => {
      const res = await api<Equipment>(`/resources/${equipmentId}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}

export function useDeleteEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (equipmentId: number) => {
      const res = await api<void>(`/resources/${equipmentId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}

/** PUT dos campos personalizados do recurso. */
export function useUpdateEquipmentEntities(equipmentId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EntityValuesUpdateBody) => {
      const res = await api<unknown>(`/resources/${equipmentId}/entities`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RESOURCES_KEY }),
  });
}
